import React, { useMemo } from 'react';
import {
  VISUAL_FIELDS,
  aggregateErrorCategory,
  aggregateRequestTrend,
  aggregateRequestsByTm,
  aggregateRequestsByUser,
  aggregateSeverity,
  aggregateWordsByUser,
} from '../utils/adminChartData';
import AdminBarChart from './AdminBarChart';
import AdminLineChart from './AdminLineChart';

function rowsForChart(field, selection, baseRows, filteredRows) {
  if (selection?.field === field) {
    return baseRows;
  }
  return filteredRows;
}

function AdminChartGrid({
  baseRows,
  filteredRows,
  selection,
  onVisualSelect,
  availableTMs = [],
}) {
  const emptyMessage = selection
    ? 'No data for current selection'
    : 'No data available';

  const requestUsers = useMemo(
    () => aggregateRequestsByUser(rowsForChart(VISUAL_FIELDS.USER, selection, baseRows, filteredRows)),
    [baseRows, filteredRows, selection],
  );
  const wordUsers = useMemo(
    () => aggregateWordsByUser(rowsForChart(VISUAL_FIELDS.USER, selection, baseRows, filteredRows)),
    [baseRows, filteredRows, selection],
  );
  const trend = useMemo(
    () => aggregateRequestTrend(filteredRows),
    [filteredRows],
  );
  const severity = useMemo(
    () => aggregateSeverity(rowsForChart(VISUAL_FIELDS.SEVERITY, selection, baseRows, filteredRows)),
    [baseRows, filteredRows, selection],
  );
  const errorCategories = useMemo(
    () => aggregateErrorCategory(
      rowsForChart(VISUAL_FIELDS.ERROR_CATEGORY, selection, baseRows, filteredRows),
    ),
    [baseRows, filteredRows, selection],
  );
  const platformTms = useMemo(
    () => aggregateRequestsByTm(
      rowsForChart(VISUAL_FIELDS.TM, selection, baseRows, filteredRows),
      availableTMs,
    ),
    [availableTMs, baseRows, filteredRows, selection],
  );

  const selectedUser = selection?.field === VISUAL_FIELDS.USER ? selection.value : null;
  const selectedTm = selection?.field === VISUAL_FIELDS.TM ? selection.value : null;
  const selectedSeverity = selection?.field === VISUAL_FIELDS.SEVERITY ? selection.value : null;
  const selectedError = selection?.field === VISUAL_FIELDS.ERROR_CATEGORY ? selection.value : null;

  return (
    <div className="admin-chart-grid">
      <AdminBarChart
        title="Translation Request Counts by User"
        items={requestUsers}
        axisLabel="Requests"
        selectedValue={selectedUser}
        emptyMessage={emptyMessage}
        onSelect={(value) => onVisualSelect({ field: VISUAL_FIELDS.USER, value })}
      />
      <AdminBarChart
        title="Translated Word Count by User"
        items={wordUsers}
        axisLabel="Words"
        selectedValue={selectedUser}
        emptyMessage={emptyMessage}
        onSelect={(value) => onVisualSelect({ field: VISUAL_FIELDS.USER, value })}
      />
      <AdminLineChart
        title="Requests Processed Trend"
        items={trend}
        emptyMessage={emptyMessage}
      />
      <AdminBarChart
        title="Severity Breakdown"
        items={severity}
        axisLabel="Percentage"
        showPercent
        selectedValue={selectedSeverity}
        emptyMessage={selection ? emptyMessage : 'No classified reviews in this range.'}
        onSelect={(value) => onVisualSelect({ field: VISUAL_FIELDS.SEVERITY, value })}
      />
      <AdminBarChart
        title="Error Category Breakdown"
        items={errorCategories}
        axisLabel="Percentage"
        showPercent
        selectedValue={selectedError}
        emptyMessage={selection ? emptyMessage : 'No classified reviews in this range.'}
        onSelect={(value) => onVisualSelect({ field: VISUAL_FIELDS.ERROR_CATEGORY, value })}
      />
      <AdminBarChart
        title="Platform / Source"
        items={platformTms}
        axisLabel="Requests"
        selectedValue={selectedTm}
        emptyMessage={emptyMessage}
        onSelect={(value) => onVisualSelect({ field: VISUAL_FIELDS.TM, value })}
      />
    </div>
  );
}

export default AdminChartGrid;
